import { useTranslation } from "react-i18next";
import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/PageHeader";
import { useQuery } from "react-query";
import { apollo } from "../../../utils/apollo";
import {
  GET_FILE_IMPORT_STATUS_COUNT,
  GET_FILE_IMPORT_LIST as GET_FILE_IMPORT_DETAILS,
} from "../../../utils/graphql";
import useIntegration from "../../../utils/useIntegration";
import SlashdataPolicyImportForm from "./forms/slashdata-policy-import-form";
import ShafafiyaPolicyImportForm from "./forms/shafafiya-policy-import-form";
import { FILE_IMPORT_STATUS } from "../../../utils/constants";
import Tabs from "../../../components/Tabs";
import {
  CheckIcon,
  ClockIcon,
  QueueListIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { cn } from "../../../utils/style";
import Loading from "../../../components/Loading";
import PolicyImportView from "./views/policy-import-view";
import COCImportResultsView from "./views/coc-import-results-view";
import SlashdataImportResultsView from "./views/slashdata-import-results-view";

function PolicyImport() {
  const { t } = useTranslation();
  const { integration, integrationUuid } = useIntegration();
  const [activeTab, setActiveTab] = useState("overview");
  const [statusTabs, setStatusTabs] = useState(FILE_IMPORT_STATUS);
  const { import_uuid: importUuid } = useParams();
  const navigate = useNavigate();

  // Fetch specific import details to determine its integration type
  const importDetailsQuery = useQuery({
    queryKey: ["file_import_details", importUuid],
    queryFn: async () => {
      const { data } = await apollo.query({
        query: GET_FILE_IMPORT_DETAILS,
        variables: {
          page: 1,
          first: 1,
          filter: { fileImportUuid: importUuid },
        },
      });
      return data?.getFileImportList?.data?.[0] ?? null;
    },
    enabled: !!importUuid,
    refetchInterval: 10000,
  });


  const importDetails = importDetailsQuery.data;

  // Helper to update or clear the active import detail view
  const setSelectedFileImport = (fi) => {
    if (fi?.fileImportUuid) {
      navigate(`/policy/import/details/${fi.fileImportUuid}`);
    } else {
      navigate("/policy/import");
    }
  };

  useQuery(
    ["file_import_status_count", integrationUuid],
    async () => {
      const { data } = await apollo.query({
        query: GET_FILE_IMPORT_STATUS_COUNT,
        variables: {
          integrationUuid,
        },
      });
      return data?.getFileImportStatusCount;
    },
    {
      keepPreviousData: false,
      refetchInterval: 10000,
      cacheTime: 10000,
      staleTime: 10000,
      onSettled: (data) => {
        if (data && Array.isArray(data)) {
          let updatedStatusTabs = [...statusTabs];
          data.forEach((item) => {
            let foundIndex = updatedStatusTabs.findIndex(
              (tab) => tab.column === item.status,
            );
            if (foundIndex !== -1) {
              updatedStatusTabs[foundIndex].count = parseInt(item.count);
            }
          });
          setStatusTabs(updatedStatusTabs);
        }
      },
    },
  );

  return (
    <DashboardLayout>
      <PageHeader
        title={t("Member Bulk Upload")}
        description={t("policy.import_policies_description")}
      >
        <Tabs
          hideOnMobile={false}
          tabs={statusTabs.map((s) => ({ ...s, name: t(s.name) }))}
          rightSide={true}
          get={activeTab}
          set={(v) => {
            setActiveTab(v);
            if (importUuid) {
              setSelectedFileImport(null);
            }
          }}
        />
      </PageHeader>
      {/* ── Results view for a specific import batch ── */}
      {importUuid && (
        <>
          {importDetailsQuery.isLoading ? (
            <Loading message="Loading import details..." />
          ) : importDetails?.integration?.integrationType === "shafafiya" ? (
            <COCImportResultsView
              fileImport={importDetails}
              onBack={() => setSelectedFileImport(null)}
            />
          ) : importDetails?.integration?.integrationType === "slashdata" ? (
            <SlashdataImportResultsView
              fileImport={importDetails}
              onBack={() => setSelectedFileImport(null)}
            />
          ) : (
            <div className="mt-6 p-12 bg-white rounded-2xl border border-slate-200 text-center text-slate-500">
              {t("Import record not found or unsupported integration type.")}
            </div>
          )}
        </>
      )}

      {!importUuid && activeTab === "overview" && (
        <div>
          <div className="mt-6">
            {!integration && (
              <div className="flex items-center justify-center h-40 text-gray-500 font-light text-xl">
                {t("Select an integration from the sidebar to continue.")}
              </div>
            )}

            {integration?.integrationType === "slashdata" && (
              <SlashdataPolicyImportForm
                integration={integration}
                onSuccess={(newUuid) => {
                  if (newUuid) {
                    setSelectedFileImport({ fileImportUuid: newUuid });
                  } else {
                    setActiveTab("processing");
                  }
                }}
              />
            )}
            {integration?.integrationType === "shafafiya" && (
              <ShafafiyaPolicyImportForm
                integration={integration}
                onSuccess={(newUuid) => {
                  if (newUuid) {
                    setSelectedFileImport({ fileImportUuid: newUuid });
                  } else {
                    setActiveTab("processing");
                  }
                }}
              />
            )}
          </div>
          <FileImportCardsOverview statusTabs={statusTabs} />
        </div>
      )}

      {!importUuid &&
        ["processing", "completed", "failed"].includes(activeTab) && (
          <PolicyImportView
            key={activeTab}
            filterStatus={activeTab}
            onViewResults={(fi) => setSelectedFileImport(fi)}
          />
        )}
    </DashboardLayout>
  );
}

function FileImportCardsOverview({ statusTabs }) {
  const { t } = useTranslation();
  const status = useMemo(() => {
    let list = {
      overview: 0,
      processing: 0,
      completed: 0,
      failed: 0,
    };
    statusTabs?.forEach((s) => {
      list[s.column] = s?.count;
    });
    return list;
  }, [statusTabs]);
  return (
    <div className="mt-16">
      <div className="flex items-center gap-3 mb-8">
        <div className="h-8 w-1.5 bg-primary-500 rounded-full shadow-[0_0_10px_rgba(59,130,246,0.5)]" />
        <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight">
          {t("System Statistics Overview")}
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <OverviewLine
          icon={<QueueListIcon className="w-6 h-6" />}
          title={t("Total Uploaded")}
          count={status.overview}
          variant="blue"
        />
        <OverviewLine
          icon={<ClockIcon className="w-6 h-6" />}
          title={t("Under Process")}
          count={status.processing}
          variant="amber"
        />
        <OverviewLine
          icon={<CheckIcon className="w-6 h-6" />}
          title={t("Successfully Processed")}
          count={status.completed}
          variant="emerald"
        />
        <OverviewLine
          icon={<XMarkIcon className="w-6 h-6" />}
          title={t("Rejected Files")}
          count={status.failed}
          variant="rose"
        />
      </div>
    </div>
  );
}

function OverviewLine({ icon, title, count, variant }) {
  const variants = {
    rose: {
      iconContainer: "bg-rose-50 text-rose-600 border-rose-100",
      count: "text-rose-600",
      accent: "bg-rose-500",
      shadow: "hover:shadow-rose-500/10",
    },
    blue: {
      iconContainer: "bg-blue-50 text-blue-600 border-blue-100",
      count: "text-blue-600",
      accent: "bg-blue-500",
      shadow: "hover:shadow-blue-500/10",
    },
    amber: {
      iconContainer: "bg-amber-50 text-amber-600 border-amber-100",
      count: "text-amber-600",
      accent: "bg-amber-500",
      shadow: "hover:shadow-amber-500/10",
    },
    emerald: {
      iconContainer: "bg-emerald-50 text-emerald-600 border-emerald-100",
      count: "text-emerald-600",
      accent: "bg-emerald-500",
      shadow: "hover:shadow-emerald-500/10",
    },
  };
  const style = variants[variant] || variants.blue;
  return (
    <div
      className={cn(
        "group relative flex flex-col items-center justify-center bg-white/70 backdrop-blur-xl rounded-2xl p-8 border border-slate-200/60 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:bg-white active:scale-[0.97]",
        style.shadow,
      )}
    >
      {/* Top Accent Line */}
      <div
        className={cn(
          "absolute top-0 start-1/2 -translate-x-1/2 h-1 w-0 group-hover:w-16 transition-all duration-300 rounded-b-full",
          style.accent,
        )}
      />

      {/* Icon Container */}
      <div
        className={cn(
          "mb-6 p-4 rounded-2xl border transition-all duration-300 group-hover:scale-110",
          style.iconContainer,
        )}
      >
        {icon}
      </div>

      {/* Title */}
      <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2 group-hover:text-slate-600 transition-colors">
        {title}
      </div>

      {/* Count */}
      <div
        className={cn(
          "text-4xl font-black tracking-tight transition-all duration-300 group-hover:scale-105",
          style.count,
        )}
      >
        {count.toLocaleString()}
      </div>

      {/* Glossy Overlay */}
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/40 to-transparent opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity" />
    </div>
  );
}

export default PolicyImport;
